import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useProductManagementData } from './useProductManagementData';
import { useProductAPI } from '../../api/product/useProductAPI';
import CrudModal from '../../components/Modal/Crud/CrudModal';
import ConfirmModal from '../../components/Modal/Confirm/ConfirmModal';
import ExcelImportResultModal from '../../components/Modal/ExcelImportResult/ExcelImportResultModal';
import ProductDetailTables from './components/ProductDetailTables';

import FileManagement from '../../components/Modal/File/FileManagement';
import BaseModal from '../../components/Modal/Base/BaseModal';
import Table from '../../components/Table/Table/Table';
import EmptyState from '../../components/EmptyState';
import Search from '../../components/Search/Search'
import { useToast } from '../../hooks/useToast';
import { LIST_EMPTY } from '../_shared/listEmptyState';
import { 
  productTableColumns,
  getFormFields,
  getSubmitFunction,
  productSearchFormFields,
  productFormFields,
  processFormFields,
  productBOMFormFields
} from './context';

import { PiFloppyDiskBold } from "react-icons/pi";
import { FaBox, FaInfo, FaIndustry  } from 'react-icons/fa';
import './ProductManagement.css';



const ProductManagement = () => {
  const product = useSelector((state) => state.product);
  const productBOM = useSelector((state) => state.productBOM);
  const processes = useSelector((state) => state.processStep);
  const session = useSelector((state) => state.session.data);
  const aa = useProductAPI();
  const toast = useToast();
  const [clickedRow, setClickedRow] = useState(null);
  const apiHandlers = useProductManagementData(clickedRow);
  const [productCount, setProductCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importResultOpen, setImportResultOpen] = useState(false);

  useEffect(() => {
      const fetchData = async () => {
          const count = await aa.product.getCount({ companySeq: session.companySeq });
          setProductCount(count);
      };
      fetchData();
  }, [product]);
  const handleProductRowClick = async (row) => {
    if (!row.productSeq) {
      console.error('Invalid product row:', row);
      return;
    }
    if (clickedRow && clickedRow.productSeq === row.productSeq) {
      setClickedRow(null);
      return;
    }
    setClickedRow(row)
  };

  const closeModal = () => {
    setIsOpen(false);
    setContent(null);
    setModalTitle('');
  };

  //  type : 1 = 등록, 2 = 수정
  //  category : 1 = 제품, 2 = 공정 단계, 3 = 제품 BOM
  const openCRUDModal = (type, category, row = null) => {
    let formField;
    let action;
    switch(category){
      case 1 :
        formField = productFormFields;
        switch(type){
          case 1 :
            action = apiHandlers.addProduct;
            setModalTitle(<><FaBox style={{ marginRight: '8px' }} /> 제품 등록</>);
            break;
          case 2 :
            action = apiHandlers.updateProduct;
            setModalTitle(<><FaBox style={{ marginRight: '8px' }} /> 제품 수정</>);
            break;
          default : return;
        }
        break;
      case 2 :
        formField = (processFormFields);
        switch(type){
          case 1 :
            action = apiHandlers.addProcessStep;
            setModalTitle(<><FaBox style={{ marginRight: '8px' }} /> 공정 순서 등록</>);
            break;
          case 2 :
            action = apiHandlers.updateProcessStep;
            setModalTitle(<><FaBox style={{ marginRight: '8px' }} /> 공정 순서 수정</>);
            break;
          default : return;
        }
        break;
      case 3 :
        formField = productBOMFormFields;
        switch(type){
          case 1 :
            action = apiHandlers.addProductBOM;
            setModalTitle(<><FaBox style={{ marginRight: '8px' }} /> BOM 등록</>);
            break;
          case 2 :
            action = apiHandlers.updateProductBOM;
            setModalTitle(<><FaBox style={{ marginRight: '8px' }} /> BOM 수정</>);
            break;
          default : return;
        }
        break;
      default : return;
    }
    
    setContent(
      <CrudModal
        type={ type }
        formFields={ formField }
        formDatas={ row || {} }
        onClose={ closeModal }
        clickedRow={ clickedRow }
        onSubmit={ action }
      />
    );
    setIsOpen(true);
  };

  const promptDelete = useCallback((type, row) => {
    if (!row) {
      return;
    }
    let message;
    let action;
    switch(type){
      case 1 :
        message = `'${row.productName}'을 제품에서\n삭제하시겠습니까?`;
        action = apiHandlers.deleteProduct;
        break;
      case 2 :
        message = `'${row.processName}'을 공정단계에서\n삭제하시겠습니까?`;
        action = apiHandlers.deleteProcessStep;
        break;
      case 3 :
        message = `'${row.materialName}'을 BOM에서\n삭제하시겠습니까?`;
        action = apiHandlers.deleteProductBOM;
        break;
      default :
        return;
    }

    setModalTitle(<><FaInfo style={{ marginRight: '8px' }} /> 알림</>);
    setContent(
      <ConfirmModal
        message={message}
        onConfirmAction={() => {
          action(row);
          closeModal(); 
        }}
        onCancelAction={closeModal} 
        confirmText="삭제"
        cancelText="취소"
      />);
    setIsOpen(true); 
  }, [apiHandlers, closeModal]);

  const handleExcelDownload = async () => {
    await aa.product.download({ companySeq: session.companySeq });
  };

  const handleExcelUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;
      const formData = new FormData();
      formData.append('file', files[0]);
      try {
        const result = await aa.product.import(formData);
        if (result && result.data) {
          setImportResult(result.data);
          setImportResultOpen(true);
        }
        apiHandlers.refreshProduct();
      } catch (e) {
        toast.error('엑셀 업로드 실패');
      }
    };
    input.click();
  };

  const openFileModal = (row) => {
    if (!row) {
      return;
    }
    setModalTitle(<><PiFloppyDiskBold size={20} style={{ marginRight: '8px' }} /> 첨부파일 관리</>);
    setIsOpen(true);
    setContent(
      <FileManagement
        row={row || clickedRow}
        fileCode={"Product"}
        onClose={closeModal} 
      />);
    
  };

  return (
    <div className="product-management">
      <Search
          search={apiHandlers.searchProduct}
          formFields={productSearchFormFields}
          title={"제품"}
      />
      
      <div className="top-table-container">
        <Table
          className="top-table"
          title={ 
                  <div className="title">
                    <span className="title-with-icon">
                      <FaBox className="icon" />
                      제품 목록 {productCount && `(${productCount})`}
                    </span>
                  </div>  
                }
          columns={productTableColumns(openCRUDModal, promptDelete, openFileModal, apiHandlers.updateProduct)} 
          data={product.data}
          page={product.page}
          onRowClick={ handleProductRowClick }
          onAddClick={ () => openCRUDModal(1, 1) }
          paging={ apiHandlers.productPaging }
          showSearchBar={ false }
          excelExport={handleExcelDownload}
          excelImport={handleExcelUpload}
          emptyState={
            (product.data?.length ?? 0) === 0 ? (
              <EmptyState
                message={LIST_EMPTY.product.message}
                hint={LIST_EMPTY.product.hint}
                ctaLabel={LIST_EMPTY.product.ctaLabel}
                onCta={() => openCRUDModal(1, 1)}
              />
            ) : null
          }
        />
      </div>
      {clickedRow && (
        <ProductDetailTables
          clickedRow={clickedRow}
          processesData={processes.data}
          processesPage={processes.page}
          processesPaging={apiHandlers.processStepPaging}
          productBOMData={productBOM.data}
          productBOMPage={productBOM.page}
          productBOMPaging={apiHandlers.productBOMPaging}
          crud={openCRUDModal} 
          promptDelete={promptDelete}
        />
      )}

      <BaseModal
        title={modalTitle}
        children={content}
        isOpen={isOpen}
        onClose={closeModal}
      />
      <ExcelImportResultModal
        isOpen={importResultOpen}
        onClose={() => setImportResultOpen(false)}
        result={importResult}
      />
    </div>
  );
};

export default ProductManagement;

